import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useAuth } from './auth.context';
import { useToast } from '../hooks/use-toast';

interface FeedbackContextType {
  isDialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  submitFeedback: (feedback: string, topic: string) => Promise<boolean>;
  isSubmitting: boolean;
  error: string | null;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

export const FeedbackProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getToken, user } = useAuth();
  const { toast } = useToast();

  const openDialog = () => {
    setIsDialogOpen(true);
    setError(null);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setError(null);
  };

  const submitFeedback = async (feedback: string, topic: string): Promise<boolean> => {
    if (!feedback.trim()) {
      setError('Please enter your feedback before submitting.');
      toast({
        title: 'Error',
        description: 'Please enter your feedback before submitting.',
        variant: 'destructive',
        duration: 3000, // Auto-dismiss after 3 seconds
      });
      return false;
    }
    
    if (feedback.trim().length < 10) {
      setError('Feedback must be at least 10 characters long.');
      toast({
        title: 'Error',
        description: 'Feedback must be at least 10 characters long.',
        variant: 'destructive',
        duration: 3000, // Auto-dismiss after 3 seconds
      });
      return false;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      const response = await fetch(`${baseUrl}/api/v1/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: feedback,
          topic: topic,
          userId: user?.id,
          userEmail: user?.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error: ${response.status} ${response.statusText}`);
      }

      toast({
        title: 'Feedback Submitted',
        description: 'Thank you for your feedback! We appreciate your input.',
        duration: 3000, // Auto-dismiss after 3 seconds
      });
      
      closeDialog();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      
      toast({
        title: 'Submission Failed',
        description: 'There was an error submitting your feedback. Please try again.',
        variant: 'destructive',
        duration: 3000, // Auto-dismiss after 3 seconds
      });
      
      console.error('Error submitting feedback:', err);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FeedbackContext.Provider value={{
      isDialogOpen,
      openDialog,
      closeDialog,
      submitFeedback,
      isSubmitting,
      error
    }}>
      {children}
    </FeedbackContext.Provider>
  );
};

export const useFeedbackContext = () => {
  const context = useContext(FeedbackContext);
  if (context === undefined) {
    throw new Error('useFeedbackContext must be used within a FeedbackProvider');
  }
  return context;
};